import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useProjectDocuments, useUploadDocuments, useDeleteDocument, getDocumentUrl, formatFileSize } from "@/hooks/use-project-documents";
import { useProjectComments } from "@/hooks/use-project-comments";
import { useUpsells } from "@/hooks/use-upsells";
import { getUpsellAttachmentUrl } from "@/hooks/use-upsell-mutations";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useProjectPermission } from "@/hooks/use-project-permission";
import { formatDate } from "@/lib/format";
import { SrIcon } from "@/components/ui/SrIcon";
import FilePreviewDialog from "@/components/shared/FilePreviewDialog";
import FileDropzone from "@/components/shared/FileDropzone";
import { ProjectAttachmentSection, type AttachmentItem } from "./ProjectAttachmentSection";

function inferMime(fileName: string): string | undefined {
  const ext = fileName.toLowerCase().split(".").pop();
  if (!ext) return undefined;
  if (ext === "pdf") return "application/pdf";
  if (["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ext)) {
    return ext === "jpg" ? "image/jpeg" : ext === "svg" ? "image/svg+xml" : `image/${ext}`;
  }
  return undefined;
}

function canPreview(fileName: string): boolean {
  const mime = inferMime(fileName);
  return mime === "application/pdf" || (mime?.startsWith("image/") ?? false);
}

interface PreviewTarget {
  url: string;
  title: string;
  fileName: string;
}

export function DokumenterTab({ projectId, offerPdfUrl }: { projectId: string; offerPdfUrl: string | null }) {
  const { data: documents = [], isLoading } = useProjectDocuments(projectId);
  // VIB-996: samle vedlegg fra prosjektoppdateringer + mersalg på samme side.
  const { data: comments = [] } = useProjectComments(projectId);
  const { data: upsells = [] } = useUpsells(projectId);
  const uploadMut = useUploadDocuments();
  const deleteMut = useDeleteDocument();
  const { activeUser: authUser } = useImpersonation();
  // VIB-722c: manage_documents gates opplasting + sletting.
  // Admin/superadmin overstyrer; default = project_lead + client_advisor=true.
  const canManageDocuments = useProjectPermission(projectId, "manage_documents");
  // VIB-2869: progress for multi-file upload ("Laster opp 2 av 5").
  const [uploadProgress, setUploadProgress] = useState<{ index: number; total: number } | null>(null);
  const [preview, setPreview] = useState<PreviewTarget | null>(null);
  const isUploading = uploadMut.isPending;

  const handleFiles = (files: File[]) => {
    if (files.length === 0 || !authUser) return;
    uploadMut.mutate(
      {
        projectId,
        files,
        uploadedById: authUser.id,
        onProgress: (index, total) => setUploadProgress({ index, total }),
      },
      { onSettled: () => setUploadProgress(null) },
    );
  };

  const handleDelete = (id: string) => {
    if (confirm("Slett dokument?")) {
      deleteMut.mutate({ id, projectId });
    }
  };

  // VIB-996: chat-vedlegg har ferdig url; flat ut alle oppdateringers vedlegg.
  const commentItems: AttachmentItem[] = comments.flatMap((c) =>
    (c.attachments ?? []).map((a) => ({
      id: a.id,
      name: a.name,
      size: a.file_size,
      meta: c.author?.full_name
        ? `${c.author.full_name} • ${formatDate(c.created_at)}`
        : formatDate(c.created_at),
      onOpen: () => window.open(a.url, "_blank", "noopener,noreferrer"),
    })),
  );

  // VIB-996: mersalg-vedlegg ligger i offers-bucket — signert url hentes on-click.
  const upsellItems: AttachmentItem[] = upsells
    .filter((u) => u.attachment_path && u.attachment_filename)
    .map((u) => ({
      id: u.id,
      name: u.attachment_filename as string,
      size: null,
      meta: formatDate(u.created_at),
      onOpen: async () => {
        const url = await getUpsellAttachmentUrl(u.attachment_path as string);
        if (url) window.open(url, "_blank", "noopener,noreferrer");
      },
    }));

  const hasAnyContent =
    documents.length > 0 ||
    !!offerPdfUrl ||
    commentItems.length > 0 ||
    upsellItems.length > 0;

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="space-y-4">
          {/* VIB-2869: klikk, tastatur eller dra-og-slipp, flere filer om gangen. */}
          {canManageDocuments && (
            <FileDropzone
              onFiles={handleFiles}
              disabled={isUploading}
              ariaLabel="Last opp dokument"
              label="Last opp dokument, eller dra filer hit"
              busyLabel={
                uploadProgress && uploadProgress.total > 1
                  ? `Laster opp ${uploadProgress.index} av ${uploadProgress.total}...`
                  : "Laster opp..."
              }
            />
          )}

          {/* Documents list */}
          <div className="space-y-2">
            {isLoading ? (
              <div className="flex justify-center py-6">
                <SrIcon name="loader2" className="w-4 h-4 animate-spin text-muted-foreground"  />
              </div>
            ) : !hasAnyContent ? (
              <p className="text-sm text-muted-foreground text-center py-4">Ingen dokumenter.</p>
            ) : (
              <>
                {(documents.length > 0 || offerPdfUrl) && (
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Dokumenter
                  </h3>
                )}
                {/* Legacy offer PDF */}
                {offerPdfUrl && (
                  <div className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/20 transition-colors">
                    <div className="w-8 h-8 bg-destructive/10 rounded flex items-center justify-center">
                      <span className="text-xs font-medium text-destructive">PDF</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">Tilbud</div>
                      <div className="text-xs text-muted-foreground">Opprinnelig tilbud</div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setPreview({ url: offerPdfUrl, title: "Opprinnelig tilbud", fileName: "tilbud.pdf" })
                      }
                      className="h-8 w-8 p-0"
                      title="Forhåndsvis"
                      aria-label="Forhåndsvis tilbud"
                    >
                      <SrIcon name="eye" className="w-4 h-4 text-muted-foreground" />
                    </Button>
                  </div>
                )}

                {/* Uploaded documents */}
                {documents.map((doc) => {
                  const url = getDocumentUrl(doc.file_path);
                  const ext = doc.file_name.split(".").pop()?.toUpperCase() || "FILE";
                  const previewable = canPreview(doc.file_name);
                  return (
                    <div
                      key={doc.id}
                      className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/20 transition-colors group"
                    >
                      <div className="w-8 h-8 bg-primary/10 rounded flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-medium text-primary">
                          {ext.slice(0, 3)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <a
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm font-medium hover:underline truncate block"
                        >
                          {doc.file_name}
                        </a>
                        <div className="text-xs text-muted-foreground space-x-2">
                          <span>{formatFileSize(doc.file_size)}</span>
                          <span>•</span>
                          <span>{formatDate(doc.created_at)}</span>
                          {doc.user && (
                            <>
                              <span>•</span>
                              <span>{doc.user.full_name}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {previewable && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              setPreview({ url, title: doc.file_name, fileName: doc.file_name })
                            }
                            className="h-8 w-8 p-0"
                            title="Forhåndsvis"
                            aria-label={`Forhåndsvis ${doc.file_name}`}
                          >
                            <SrIcon name="eye" className="w-4 h-4 text-muted-foreground" />
                          </Button>
                        )}
                        {canManageDocuments && (
                          <div className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 group-focus-within:opacity-100 transition-opacity">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(doc.id)}
                              disabled={deleteMut.isPending}
                              className="h-8 w-8 p-0"
                              title="Slett dokument"
                              aria-label={`Slett ${doc.file_name}`}
                            >
                              {deleteMut.isPending ? (
                                <SrIcon name="loader2" className="w-4 h-4 animate-spin"  />
                              ) : (
                                <SrIcon name="trash2" className="w-4 h-4 text-destructive"  />
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* VIB-996: read-only vedlegg fra andre kilder. */}
                <ProjectAttachmentSection
                  title="Fra prosjektoppdateringer"
                  items={commentItems}
                />
                <ProjectAttachmentSection title="Fra mersalg" items={upsellItems} />
              </>
            )}
          </div>
        </div>
      </CardContent>
      {preview && (
        <FilePreviewDialog
          open={!!preview}
          onOpenChange={(o) => !o && setPreview(null)}
          url={preview.url}
          title={preview.title}
          fileName={preview.fileName}
          mimeType={inferMime(preview.fileName)}
        />
      )}
    </Card>
  );
}
